import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Icon, IconName } from "./Icon";

interface UserMemory {
  id: string;
  username: string;
  fact?: string;
  content?: string;
  category: "personal" | "preference" | "experience" | "relationship";
  confidence?: number;
  createdAt: string;
  updatedAt?: string;
  lastReferencedAt?: string;
  referenceCount?: number;
  metadata?: {
    keywords?: string[];
    confidence?: number;
  };
}

interface MemoryViewerProps {
  authToken: string;
  defaultMode?: "exact" | "semantic";
}

const MemoryViewer: React.FC<MemoryViewerProps> = ({
  authToken,
  defaultMode = "exact",
}) => {
  const navigate = useNavigate();
  const [memories, setMemories] = useState<UserMemory[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchUsername, setSearchUsername] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchMode, setSearchMode] = useState<"exact" | "semantic">(
    defaultMode,
  );
  const [semanticQuery, setSemanticQuery] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    memoryId: string | null;
    memoryContent: string;
  }>({ isOpen: false, memoryId: null, memoryContent: "" });
  const [deletingMemoryId, setDeletingMemoryId] = useState<string | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const fetchMemories = async (username: string) => {
    if (!username) return;

    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(`/api/bot/memories/${username}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Cache-Control": "no-cache",
        },
        params: {
          _t: new Date().getTime(), // Cache buster
        },
      });
      setMemories(response.data.memories || []);
    } catch (error) {
      console.error("Error fetching memories:", error);
      setMemories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchMode === "exact") {
      fetchMemories(searchUsername.toLowerCase());
    } else {
      performSemanticSearch();
    }
  };

  const performSemanticSearch = async () => {
    if (!semanticQuery) return;

    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(`/api/bot/memories/search`, {
        params: {
          q: semanticQuery,
          username: searchUsername || undefined,
          limit: 20,
        },
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Cache-Control": "no-cache",
        },
      });

      console.log("Search response:", response.status, response.data);

      // Handle the response
      if (response.data && (response.data.results || response.data.memories)) {
        setMemories(response.data.results || response.data.memories || []);
      } else {
        console.log("No data in response, response:", response);
        setMemories([]);
      }
    } catch (error) {
      console.error("Error performing semantic search:", error);
      setMemories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (memoryId: string, memoryContent: string) => {
    setDeleteConfirmation({
      isOpen: true,
      memoryId,
      memoryContent,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.memoryId) return;

    setDeletingMemoryId(deleteConfirmation.memoryId);
    try {
      await axios.delete(`/api/bot/memories/${deleteConfirmation.memoryId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Remove the deleted memory from the list
      setMemories((prevMemories) =>
        prevMemories.filter((m) => m.id !== deleteConfirmation.memoryId),
      );

      // Close the confirmation dialog
      setDeleteConfirmation({
        isOpen: false,
        memoryId: null,
        memoryContent: "",
      });
    } catch (error) {
      console.error("Error deleting memory:", error);
      alert("Failed to delete memory. Please try again.");
    } finally {
      setDeletingMemoryId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ isOpen: false, memoryId: null, memoryContent: "" });
  };

  const getCategoryIcon = (category: string, content?: string) => {
    // Check content for specific topics
    if (content) {
      const lowerContent = content.toLowerCase();
      if (
        lowerContent.includes("music") ||
        lowerContent.includes("synth") ||
        lowerContent.includes("sound")
      ) {
        return "music";
      }
      if (lowerContent.includes("game") || lowerContent.includes("play")) {
        return "gamepad-2";
      }
      if (
        lowerContent.includes("who am i") ||
        lowerContent.includes("who i am")
      ) {
        return "smile";
      }
    }

    switch (category) {
      case "personal":
        return "user";
      case "preference":
        return "heart";
      case "experience":
        return "star";
      case "relationship":
        return "handshake";
      default:
        return "pencil";
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "personal":
        return "#3B82F6";
      case "preference":
        return "#EC4899";
      case "experience":
        return "#F59E0B";
      case "relationship":
        return "#10B981";
      default:
        return "#6B7280";
    }
  };

  const getConfidenceBorder = (confidence?: number) => {
    if (!confidence) return "2px solid #4a5568";
    if (confidence >= 0.8) return "2px solid #48bb78"; // Solid green
    if (confidence >= 0.5) return "2px dashed #f6ad55"; // Dashed orange
    return "2px dotted #fc8181"; // Dotted red
  };

  const formatMemoryContent = (content: string) => {
    // Remove @mentions
    let cleaned = content.replace(/@\w+\s*/g, "");
    // Capitalize first letter
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    // Truncate if too long
    if (cleaned.length > 150) {
      return cleaned.substring(0, 147) + "...";
    }
    return cleaned;
  };

  const getMemoryTitle = (content: string) => {
    const lowerContent = content.toLowerCase();
    if (lowerContent.includes("synth") || lowerContent.includes("music")) {
      return "Music Production Preference";
    }
    if (
      lowerContent.includes("who am i") ||
      lowerContent.includes("who i am")
    ) {
      return "Identity Query";
    }
    if (lowerContent.includes("style")) {
      return "Style Preference";
    }
    return "Personal Note";
  };

  const filteredMemories =
    selectedCategory === "all"
      ? memories
      : memories.filter((m) => m.category === selectedCategory);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        gap: "20px",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
      }}
    >
      <div
        style={{
          flex: "1",
          maxWidth: isMobile ? "100%" : "700px",
          width: "100%",
          order: 1,
          marginRight: isMobile ? "0" : "20px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? "16px" : "0",
            marginBottom: "24px",
          }}
        >
          <h3 style={{ fontSize: "20px", margin: 0, color: "#e2e8f0" }}>
            User Memory Bank
          </h3>
        </div>

        {/* Search Mode Toggle */}
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            gap: "12px",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setSearchMode("exact");
              setMemories([]);
              setSearchUsername("");
              setSemanticQuery("");
              setHasSearched(false);
              navigate("/dashboard/memorybank/usermemories/search");
            }}
            style={{
              padding: "8px 16px",
              border: "2px solid",
              borderColor: searchMode === "exact" ? "#9146FF" : "#4a5568",
              backgroundColor: searchMode === "exact" ? "#9146FF" : "#2d3748",
              color: searchMode === "exact" ? "white" : "#cbd5e0",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: searchMode === "exact" ? "bold" : "normal",
              flex: isMobile ? "1" : "auto",
              textAlign: "center",
            }}
          >
            Exact Search
          </button>
          <button
            type="button"
            onClick={() => {
              setSearchMode("semantic");
              setMemories([]);
              setSearchUsername("");
              setSemanticQuery("");
              setHasSearched(false);
              navigate("/dashboard/memorybank/usermemories/query");
            }}
            style={{
              padding: "8px 16px",
              border: "2px solid",
              borderColor: searchMode === "semantic" ? "#9146FF" : "#4a5568",
              backgroundColor:
                searchMode === "semantic" ? "#9146FF" : "#2d3748",
              color: searchMode === "semantic" ? "white" : "#cbd5e0",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: searchMode === "semantic" ? "bold" : "normal",
              flex: isMobile ? "1" : "auto",
              textAlign: "center",
            }}
          >
            <Icon
              name="search"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "4px" }}
            />
            AI-Powered Search
          </button>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} style={{ marginBottom: "24px" }}>
          <div
            style={{ display: "flex", gap: "12px", flexDirection: "column" }}
          >
            {searchMode === "semantic" && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    color: "#cbd5e0",
                  }}
                >
                  Search Query (ask anything about memories)
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  <input
                    type="text"
                    value={semanticQuery}
                    onChange={(e) => setSemanticQuery(e.target.value)}
                    placeholder="e.g., music, games, hobbies"
                    style={{
                      flex: 1,
                      padding: "10px 16px",
                      border: "1px solid #4a5568",
                      backgroundColor: "#1a202c",
                      color: "#e2e8f0",
                      borderRadius: "8px",
                      fontSize: "16px",
                    }}
                  />
                  {!isMobile && (
                    <div
                      style={{
                        width: "auto",
                        padding: "10px 30px",
                        visibility: "hidden",
                      }}
                    >
                      <Icon
                        name="search"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Search
                    </div>
                  )}
                </div>
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <input
                type="text"
                value={searchUsername}
                onChange={(e) => setSearchUsername(e.target.value)}
                placeholder={
                  searchMode === "exact"
                    ? "Enter username to view memories"
                    : "Filter by username (optional)"
                }
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  border: "1px solid #4a5568",
                  backgroundColor: "#1a202c",
                  color: "#e2e8f0",
                  borderRadius: "8px",
                  fontSize: "16px",
                }}
              />
              <button
                type="submit"
                disabled={
                  (searchMode === "exact" ? !searchUsername : !semanticQuery) ||
                  loading
                }
                style={{
                  padding: "10px 30px",
                  backgroundColor: "#9146FF",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "16px",
                  cursor:
                    (searchMode === "exact"
                      ? !searchUsername
                      : !semanticQuery) || loading
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    (searchMode === "exact"
                      ? !searchUsername
                      : !semanticQuery) || loading
                      ? 0.5
                      : 1,
                  width: isMobile ? "100%" : "auto",
                  minWidth: isMobile ? "auto" : "120px",
                }}
              >
                {loading ? (
                  "Searching..."
                ) : searchMode === "semantic" ? (
                  <>
                    <Icon
                      name="search"
                      size={14}
                      style={{ verticalAlign: "-2px", marginRight: "4px" }}
                    />
                    Search
                  </>
                ) : (
                  "Search"
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Category Filter */}
        {memories.length > 0 && (
          <div
            style={{
              marginBottom: "16px",
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              justifyContent: isMobile ? "center" : "flex-start",
            }}
          >
            <button
              onClick={() => setSelectedCategory("all")}
              style={{
                padding: "6px 16px",
                borderRadius: "20px",
                border: "none",
                fontSize: isMobile ? "12px" : "14px",
                cursor: "pointer",
                backgroundColor:
                  selectedCategory === "all" ? "#9146FF" : "#2d3748",
                color: selectedCategory === "all" ? "white" : "#cbd5e0",
              }}
            >
              All ({memories.length})
            </button>
            {["personal", "preference", "experience", "relationship"].map(
              (cat) => {
                const count = memories.filter((m) => m.category === cat).length;
                if (count === 0) return null;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    style={{
                      padding: "6px 16px",
                      borderRadius: "20px",
                      border: "none",
                      fontSize: isMobile ? "12px" : "14px",
                      cursor: "pointer",
                      backgroundColor:
                        selectedCategory === cat ? "#9146FF" : "#2d3748",
                      color: selectedCategory === cat ? "white" : "#cbd5e0",
                      textTransform: "capitalize",
                    }}
                  >
                    {cat} ({count})
                  </button>
                );
              },
            )}
          </div>
        )}

        {/* Search Results Info */}
        {memories.length > 0 && searchMode === "semantic" && hasSearched && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#374151",
              borderRadius: "8px",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>
              <Icon name="bot" size={16} />
            </span>
            <span style={{ fontSize: "14px", color: "#E0E7FF" }}>
              Found {memories.length} memories matching your query using
              AI-powered search
            </span>
          </div>
        )}

        {/* Memories List */}
        {filteredMemories.length > 0 ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {filteredMemories.map((memory) => (
              <div
                key={memory.id}
                style={{
                  border: getConfidenceBorder(memory.confidence),
                  borderRadius: "12px",
                  padding: "20px",
                  backgroundColor: "#1a202c",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0, 0, 0, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0, 0, 0, 0.3)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        marginBottom: "12px",
                      }}
                    >
                      <span style={{ fontSize: "24px" }}>
                        <Icon
                          name={
                            getCategoryIcon(
                              memory.category,
                              memory.content || memory.fact,
                            ) as IconName
                          }
                          size={24}
                        />
                      </span>
                      <div style={{ flex: 1 }}>
                        <h3
                          style={{
                            margin: 0,
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#e2e8f0",
                          }}
                        >
                          {getMemoryTitle(memory.content || memory.fact || "")}
                        </h3>
                        <span
                          style={{
                            fontSize: "14px",
                            color: "#9CA3AF",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            marginTop: "4px",
                          }}
                        >
                          <Icon
                            name="user"
                            size={14}
                            style={{
                              verticalAlign: "-2px",
                              marginRight: "4px",
                            }}
                          />
                          {memory.username}
                        </span>
                      </div>
                    </div>
                    <p
                      style={{
                        margin: "8px 0 16px 0",
                        color: "#cbd5e0",
                        fontSize: "15px",
                        lineHeight: "1.5",
                        fontStyle: "italic",
                      }}
                    >
                      &quot;
                      {formatMemoryContent(memory.content || memory.fact || "")}
                      &quot;
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "500",
                          backgroundColor:
                            getCategoryColor(memory.category) + "20",
                          color: getCategoryColor(memory.category),
                        }}
                      >
                        {memory.category}
                      </span>
                      {memory.confidence && (
                        <span
                          style={{
                            fontSize: "12px",
                            color:
                              memory.confidence >= 0.8
                                ? "#48bb78"
                                : memory.confidence >= 0.5
                                  ? "#f6ad55"
                                  : "#fc8181",
                            fontWeight: "500",
                          }}
                        >
                          {memory.confidence >= 0.8
                            ? "High"
                            : memory.confidence >= 0.5
                              ? "Medium"
                              : "Low"}{" "}
                          confidence
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: isMobile ? "left" : "right",
                      fontSize: "12px",
                      color: "#94a3b8",
                      marginLeft: isMobile ? "0" : "16px",
                      marginTop: isMobile ? "12px" : "0",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isMobile ? "space-between" : "flex-end",
                        gap: "12px",
                        marginBottom: "4px",
                      }}
                    >
                      <p style={{ margin: 0 }}>
                        <Icon
                          name="calendar"
                          size={14}
                          style={{ verticalAlign: "-2px", marginRight: "4px" }}
                        />
                        {new Date(
                          memory.createdAt || memory.updatedAt || Date.now(),
                        ).toLocaleDateString()}{" "}
                        •{" "}
                        {new Date(
                          memory.createdAt || memory.updatedAt || Date.now(),
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                      <button
                        onClick={() =>
                          handleDeleteClick(
                            memory.id,
                            memory.content || memory.fact || "",
                          )
                        }
                        disabled={deletingMemoryId === memory.id}
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#DC2626",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "12px",
                          cursor:
                            deletingMemoryId === memory.id
                              ? "not-allowed"
                              : "pointer",
                          opacity: deletingMemoryId === memory.id ? 0.5 : 1,
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (deletingMemoryId !== memory.id) {
                            e.currentTarget.style.backgroundColor = "#B91C1C";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#DC2626";
                        }}
                      >
                        <Icon
                          name="trash-2"
                          size={14}
                          style={{ verticalAlign: "-2px", marginRight: "4px" }}
                        />
                        Delete
                      </button>
                    </div>
                    {memory.referenceCount && memory.referenceCount > 0 && (
                      <p style={{ margin: 0 }}>
                        Referenced {memory.referenceCount} time
                        {memory.referenceCount !== 1 ? "s" : ""}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : hasSearched && !loading && memories.length === 0 ? (
          <p
            style={{ textAlign: "center", color: "#cbd5e0", padding: "40px 0" }}
          >
            {searchMode === "semantic"
              ? "No memories found matching your search. Try different keywords or check if memories have been created for users."
              : "No memories found"}
          </p>
        ) : (
          <p
            style={{ textAlign: "center", color: "#cbd5e0", padding: "40px 0" }}
          >
            Enter a username to view their stored memories
          </p>
        )}
      </div>

      {/* Memory Bank Info Panel */}
      <div
        style={{
          width: isMobile ? "100%" : "350px",
          maxWidth: isMobile ? "100%" : "350px",
          backgroundColor: "rgba(45, 55, 72, 0.95)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          borderRadius: "8px",
          padding: "20px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          height: "fit-content",
          position: isMobile ? "static" : "sticky",
          top: "100px",
          order: isMobile ? 2 : 2,
          marginTop: isMobile ? "0" : "0",
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{
            color: "#e2e8f0",
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "18px",
          }}
        >
          <Icon
            name="brain"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Memory Bank Guide
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="search"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Search Modes
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>Exact Search</strong> - Find memories for a specific user
            </li>
            <li>
              <strong>AI-Powered</strong> - Search across all memories with
              natural language
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="folder"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Memory Categories
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <Icon
                name="user"
                size={14}
                style={{ verticalAlign: "-2px", marginRight: "4px" }}
              />
              <strong>Personal</strong> - Facts about the user
            </li>
            <li>
              <Icon
                name="heart"
                size={14}
                style={{ verticalAlign: "-2px", marginRight: "4px" }}
              />
              <strong>Preference</strong> - Likes and dislikes
            </li>
            <li>
              <Icon
                name="star"
                size={14}
                style={{ verticalAlign: "-2px", marginRight: "4px" }}
              />
              <strong>Experience</strong> - Past events & stories
            </li>
            <li>
              <Icon
                name="handshake"
                size={14}
                style={{ verticalAlign: "-2px", marginRight: "4px" }}
              />
              <strong>Relationship</strong> - Social connections
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="bot"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            How Memories Work
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            HAL automatically extracts and stores important information from
            conversations. These memories help personalize future interactions.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="lightbulb"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            AI Search Examples
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>&quot;What games do users like?&quot;</li>
            <li>&quot;Find memories about music&quot;</li>
            <li>&quot;Show birthday information&quot;</li>
            <li>&quot;List all pet-related memories&quot;</li>
          </ul>
        </div>

        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "12px",
            borderRadius: "6px",
            marginTop: "15px",
            border: "1px solid #4a5568",
          }}
        >
          <p
            style={{
              color: "#DC2626",
              fontSize: "12px",
              margin: "0 0 6px 0",
              fontWeight: "bold",
            }}
          >
            <Icon
              name="lock"
              size={14}
              color="#DC2626"
              style={{ verticalAlign: "-2px", marginRight: "4px" }}
            />
            Admin Only Feature
          </p>
          <p style={{ color: "#a0aec0", fontSize: "11px", margin: 0 }}>
            This feature is restricted to administrators for privacy protection.
          </p>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={handleDeleteCancel}
        >
          <div
            style={{
              backgroundColor: "#1a202c",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              width: "100%",
              border: "2px solid #4a5568",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                color: "#e2e8f0",
                fontSize: "20px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Icon
                name="alert-triangle"
                size={16}
                color="#F59E0B"
                style={{ verticalAlign: "-2px", marginRight: "4px" }}
              />
              Delete Memory?
            </h3>
            <p style={{ color: "#cbd5e0", marginBottom: "20px" }}>
              Are you sure you want to delete this memory? This action cannot be
              undone.
            </p>
            <div
              style={{
                backgroundColor: "#2d3748",
                padding: "12px",
                borderRadius: "8px",
                marginBottom: "24px",
                border: "1px solid #4a5568",
              }}
            >
              <p
                style={{
                  color: "#a0aec0",
                  margin: 0,
                  fontSize: "14px",
                  fontStyle: "italic",
                }}
              >
                &quot;{formatMemoryContent(deleteConfirmation.memoryContent)}
                &quot;
              </p>
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                flexDirection: isMobile ? "column-reverse" : "row",
              }}
            >
              <button
                onClick={handleDeleteCancel}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#4a5568",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  cursor: "pointer",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deletingMemoryId !== null}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#DC2626",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  cursor: deletingMemoryId !== null ? "not-allowed" : "pointer",
                  opacity: deletingMemoryId !== null ? 0.5 : 1,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                {deletingMemoryId !== null ? "Deleting..." : "Delete Memory"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryViewer;
